import { useCallback, useRef, useState } from 'react';

// Exit animation duration in ms. Must match the CSS animation length in
// packages/ui/styles/_toast.scss (.toast--closing) so removed toasts get a
// chance to fade/slide out before being unmounted from the DOM.
const EXIT_MS = 260;

// Default lifetime of a toast in ms before it auto-dismisses (10s).
const DEFAULT_DURATION = 10000;

// Normalize the free-form `type` values used across pages into the set the
// Toast component actually styles. Without this, `danger`/`warning` toasts
// would not get their intended icon/color.
function normalizeType(type) {
  switch (type) {
    case 'error':
    case 'danger':
      return 'error';
    case 'info':
    case 'warning':
      return 'info';
    case 'success':
    default:
      return 'success';
  }
}

/**
 * useToast — shared toast state management with auto-dismiss.
 *
 * Returns stable `pushToast` and `dismissToast` callbacks plus the current
 * `toasts` array to feed into the `<Toast>` component:
 *
 *   const { toasts, pushToast, dismissToast } = useToast();
 *   pushToast('Berhasil', 'Data telah disimpan.');
 *   pushToast('Gagal', err.message, 'error');
 *   pushToast('Info', 'Detail.', 'info', { duration: 5000 });
 *
 * Toasts are automatically dismissed after `duration` (default 10s). They
 * first transition into a `leaving` state (triggering the exit animation in
 * the Toast component) and are then removed from state shortly after.
 */
export function useToast() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismissToast = useCallback((id) => {
    // Mark as leaving so the exit animation plays, then remove after EXIT_MS.
    setToasts((list) =>
      list.map((t) => (t.id === id ? { ...t, leaving: true } : t))
    );
    window.setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id));
    }, EXIT_MS);
  }, []);

  const pushToast = useCallback(
    (title, message, type = 'success', options = {}) => {
      let resolvedTitle = title;
      let resolvedMessage = message;
      let resolvedType = type;
      let duration = options?.duration ?? DEFAULT_DURATION;

      // Gracefully handle object argument: pushToast({ title, message, type, duration })
      if (title && typeof title === 'object') {
        const payload = title;
        resolvedType = payload.type || 'success';
        resolvedTitle =
          payload.title ||
          (resolvedType === 'error' || resolvedType === 'danger'
            ? 'Gagal'
            : resolvedType === 'info' || resolvedType === 'warning'
              ? 'Perhatian'
              : 'Berhasil');
        resolvedMessage = payload.message || '';
        if (payload.duration != null) {
          duration = payload.duration;
        }
      }

      const id = ++idRef.current;
      const normalized = {
        id,
        title: resolvedTitle != null ? String(resolvedTitle) : '',
        message: resolvedMessage != null ? String(resolvedMessage) : '',
        type: normalizeType(resolvedType),
      };
      setToasts((list) => [...list, normalized]);

      if (duration > EXIT_MS) {
        // Transition into the leaving state just before the removal time so
        // the exit animation can play fully before the toast is unmounted.
        window.setTimeout(() => {
          setToasts((list) =>
            list.map((t) => (t.id === id ? { ...t, leaving: true } : t))
          );
        }, duration - EXIT_MS);
        window.setTimeout(() => {
          setToasts((list) => list.filter((t) => t.id !== id));
        }, duration);
      } else {
        window.setTimeout(() => {
          setToasts((list) => list.filter((t) => t.id !== id));
        }, Math.max(duration, 0));
      }

      return id;
    },
    []
  );

  return { toasts, pushToast, dismissToast };
}
